/**
 * Validation utilities for draft campaign functionality
 * Comprehensive input validation and security implementation
 */

export interface ValidationResult {
	isValid: boolean;
	errors: string[];
}

export interface TitleValidationOptions {
	minLength?: number;
	maxLength?: number;
	allowEmptySpaces?: boolean;
	forbiddenPatterns?: RegExp[];
	customValidator?: (title: string) => string | null;
}

/**
 * Validates draft campaign titles with comprehensive security and UX checks
 */
export const validateDraftTitle = (title: string, options: TitleValidationOptions = {}): ValidationResult => {
	const { minLength = 1, maxLength = 100, allowEmptySpaces = false, forbiddenPatterns = [], customValidator } = options;

	const errors: string[] = [];

	// Sanitize input
	const trimmedTitle = title?.trim() || '';

	// Basic validation
	if (!trimmedTitle) {
		errors.push('Campaign title is required');
		return { isValid: false, errors };
	}

	// Length validation
	if (trimmedTitle.length < minLength) {
		errors.push(`Title must be at least ${minLength} character${minLength !== 1 ? 's' : ''}`);
	}

	if (trimmedTitle.length > maxLength) {
		errors.push(`Title must be less than ${maxLength} characters`);
	}

	// Whitespace validation
	if (!allowEmptySpaces && /^\s+$/.test(title)) {
		errors.push('Title cannot contain only spaces');
	}

	// Security validation - prevent XSS and injection attacks
	const dangerousPatterns = [
		/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, // Script tags
		/javascript:/gi, // Javascript protocols
		/on\w+\s*=/gi, // Event handlers
		/data:text\/html/gi, // Data URLs
		/vbscript:/gi, // VBScript protocols
		...forbiddenPatterns,
	];

	for (const pattern of dangerousPatterns) {
		if (pattern.test(trimmedTitle)) {
			errors.push('Title contains invalid characters');
			break;
		}
	}

	// Control characters validation
	// eslint-disable-next-line no-control-regex
	if (/[\x00-\x1F\x7F-\x9F]/.test(trimmedTitle)) {
		errors.push('Title cannot contain control characters');
	}

	// Custom validation
	if (customValidator) {
		const customError = customValidator(trimmedTitle);
		if (customError) {
			errors.push(customError);
		}
	}

	return {
		isValid: errors.length === 0,
		errors,
	};
};

/**
 * Sanitizes draft title input for safe storage and display
 */
export const sanitizeDraftTitle = (title: string): string => {
	if (!title) return '';

	// Remove dangerous characters while preserving readability
	return (
		title
			.trim()
			// eslint-disable-next-line no-control-regex
			.replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Remove control characters
			.replace(/<[^>]*>/g, '') // Remove HTML tags
			.replace(/javascript:/gi, '') // Remove javascript protocols
			.replace(/vbscript:/gi, '') // Remove vbscript protocols
			.slice(0, 100)
	); // Enforce max length
};

/**
 * Validates title uniqueness (placeholder for future implementation)
 */
export const validateTitleUniqueness = async (_title: string, _excludeId?: string): Promise<ValidationResult> => {
	// TODO: Implement actual uniqueness check against backend API
	// For now, return valid to not block functionality
	return { isValid: true, errors: [] };
};

/**
 * Combined validation for draft titles with all checks
 */
export const validateDraftTitleComprehensive = async (
	title: string,
	currentTitleForComparison?: string,
	_templateId?: string
): Promise<ValidationResult> => {
	// Skip validation if title hasn't changed
	if (currentTitleForComparison && title.trim() === currentTitleForComparison.trim()) {
		return { isValid: true, errors: [] };
	}

	// Basic validation
	const basicValidation = validateDraftTitle(title, {
		maxLength: 100,
		customValidator: trimmedTitle => {
			// Business rule: prevent duplicate consecutive spaces
			if (/\s{3,}/.test(trimmedTitle)) {
				return 'Title cannot contain more than two consecutive spaces';
			}

			// Business rule: prevent leading/trailing special characters
			if (/^[^\w\s]|[^\w\s]$/.test(trimmedTitle)) {
				return 'Title cannot start or end with special characters';
			}

			return null;
		},
	});

	if (!basicValidation.isValid) {
		return basicValidation;
	}

	// Future: Check uniqueness
	// const uniquenessValidation = await validateTitleUniqueness(title, templateId);
	// if (!uniquenessValidation.isValid) {
	//   return uniquenessValidation;
	// }

	return { isValid: true, errors: [] };
};
